import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { useRoute, useNavigation } from "@react-navigation/native";
import { RouteProp } from "@react-navigation/native";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import { useGetAiTutorQuery } from "../graphql/generated/graphql";
import { useAppState } from "../contexts/AppStateContext";
import BlankLayout from "../layouts/BlankLayout";
import InteractiveLearningIntroModal from "../containers/companion/InteractiveLearningIntroModal";
import LearningGoalsContainer, { Objective } from "../containers/companion/LearningGoalsContainer";
import { colors, radii, shadowStyles, spacing, typographyStyles } from "../styles/design-system";
import { useAuth } from "../hooks/useAuth";
import ExplorationMenu, { ExplorationMediaItem } from "../components/learning-activity/ExplorationMenu/ExplorationMenu";

const mockActivities = [
  {
    id: "1",
    title: "Major Perspectives in Psychology",
    objectives: [
      { id: "obj-1", objective: "Understand behavioral theory", isCompleted: true },
      { id: "obj-2", objective: "Compare cognitive approaches", isCompleted: false },
      { id: "obj-3", objective: "Review biological foundations", isCompleted: false },
      { id: "obj-4", objective: "Connect theories to real-world cases", isCompleted: false },
    ],
  },
  {
    id: "2",
    title: "Linear Equations and Graphing",
    objectives: [
      { id: "obj-1", objective: "Solve linear equations", isCompleted: true },
      { id: "obj-2", objective: "Convert equation forms", isCompleted: true },
      { id: "obj-3", objective: "Graph lines on a plane", isCompleted: false },
      { id: "obj-4", objective: "Identify slope and intercepts", isCompleted: false },
    ],
  },
  {
    id: "3",
    title: "Photosynthesis and Cellular Respiration",
    objectives: [
      { id: "obj-1", objective: "Describe photosynthesis steps", isCompleted: true },
      { id: "obj-2", objective: "Explain cellular respiration", isCompleted: false },
      { id: "obj-3", objective: "Compare energy pathways", isCompleted: false },
    ],
  },
];

const mockExplorationItems: ExplorationMediaItem[] = [
  {
    id: "media-1",
    title: "Behaviorism in 5 Minutes",
    type: "video",
    metadata: "5:08",
    thumbnail: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=200&h=200&fit=crop",
    videoId: "zlKuojthuNo",
    description: "A concise walkthrough of behavioral theory fundamentals.",
  },
  {
    id: "media-2",
    title: "Cognitive Map Overview",
    type: "image",
    metadata: "1280×720",
    thumbnail: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=200&h=200&fit=crop",
    mediaUrl: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1200&h=800&fit=crop",
    description: "A high-resolution map summarizing key cognitive structures.",
  },
  {
    id: "media-3",
    title: "Neurons and Synapses",
    type: "video",
    metadata: "7:41",
    thumbnail: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=200&h=200&fit=crop",
    videoId: "tTfYd_HXC08",
    description: "Neural signaling explained with quick visual callouts.",
  },
  {
    id: "media-4",
    title: "Perspective Comparison Chart",
    type: "image",
    metadata: "1920×1080",
    thumbnail: "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?w=200&h=200&fit=crop",
    mediaUrl: "https://images.unsplash.com/photo-1487017159836-4e23ece2e4cf?w=1200&h=800&fit=crop",
    description: "Side-by-side chart comparing core learning perspectives.",
  },
  {
    id: "media-5",
    title: "Conditioning vs. Reinforcement",
    type: "video",
    metadata: "8:02",
    thumbnail: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=200&h=200&fit=crop",
    videoId: "gHkELWFqGKQ",
    description: "A quick comparison of conditioning frameworks and outcomes.",
  },
  {
    id: "media-6",
    title: "Brain Regions Quick Map",
    type: "image",
    metadata: "1600×900",
    thumbnail: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=200&h=200&fit=crop",
    mediaUrl: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1200&h=800&fit=crop",
    description: "Key regions highlighted for quick reference.",
  },
  {
    id: "media-7",
    title: "Memory Models Overview",
    type: "video",
    metadata: "10:31",
    thumbnail: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=200&h=200&fit=crop",
    videoId: "zlKuojthuNo",
    description: "An overview of short- and long-term memory models.",
  },
];

const LearningActivityView = () => {
  const route = useRoute<RouteProp<RootStackParamList, "Interactive Learning Environment">>();
  const { activityId } = route.params || {};
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [introModalVisible, setIntroModalVisible] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);

  const { handleHideLearningActivityIntro } = useAppState();

  const { meData } = useAuth();
  const { data: aiTutorData, loading: aiTutorLoading } = useGetAiTutorQuery({
    variables: { id: activityId },
    skip: !activityId,
  });

  const activityInfo = aiTutorData?.getAITutor?.data[0];
  const fallbackActivity = useMemo(
    () =>
      mockActivities.find((activity) => activity.id === activityId) ??
      mockActivities[0] ?? { id: "fallback", title: "Learning Activity", objectives: [] },
    [activityId]
  );
  const activityTitle = activityInfo?.title ?? fallbackActivity.title ?? "Learning Activity";
  const objectives: Objective[] =
    (activityInfo?.objectives as Objective[] | undefined) ?? (fallbackActivity.objectives as Objective[]);

  const handleExitPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("Learning Activities");
    }
  };

  const handleCloseIntroModal = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      handleHideLearningActivityIntro();
    }
    setIntroModalVisible(false);
  };

  useEffect(() => {
    if (meData?.me?.showLearningActivityIntro) {
      setIntroModalVisible(true);
    }
  }, [meData?.me]);

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomButton
              onPress={handleExitPress}
              size="s"
              text="Back to course"
              styleType="secondary"
              onHover={(hovered) => setIsBackHovered(hovered)}
              style={[styles.backButton, isBackHovered && styles.backButtonHover]}
              textStyle={styles.backButtonText}
              accessibilityLabel="Exit button"
            />
            {/* Display the activity name */}
            <CustomText
              text={activityTitle}
              useTranslationText={false}
              weight="bold"
              size="m"
              accessibilityLabel="Selected resource title"
              aria-level="1"
              role="heading"
              style={{ color: colors.grey[900] }}
            />
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.content}>
          {!aiTutorLoading && <LearningGoalsContainer objectives={objectives} />}
          <ExplorationMenu items={mockExplorationItems} />
        </View>

        <InteractiveLearningIntroModal visible={introModalVisible} onClose={handleCloseIntroModal} />
      </View>
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomColor: colors.grey[200],
    borderBottomWidth: 1,
  },
  headerLeft: {
    paddingHorizontal: spacing.lg,
    paddingVertical: 10,
    gap: spacing.sm,
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    minHeight: 36,
    padding: 0,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    backgroundColor: colors.grey[100],
    ...shadowStyles.none,
  },
  backButtonHover: {
    backgroundColor: colors.grey[200],
  },
  backButtonText: {
    ...typographyStyles.textSm,
    ...typographyStyles.fontRegular,
    color: colors.grey[900],
  },
  content: {
    flex: 1,
    flexDirection: "row",
    position: "relative",
    backgroundColor: colors.grey[50],
  },
});

export default LearningActivityView;
